import { useSuspenseQuery } from "@apollo/client";
import {
  BreadcrumbArray,
  CurrencyAmountUI,
  DateUI,
  PrimaryFullScreenSpinner,
  StatusUI,
  TransferGroupDescription,
  UserProfile,
  uppercaseStatusToStatus,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { PaneActions } from "~/components/operations/actions";
import { TransferGroupUI } from "~/components/TransferGroup/TransferGroupUI";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/transfer-groups_/$transferGroupId",
)({
  head: () => opsHead("Transfer Group"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { transferGroupId } = Route.useParams();
  if (!transferGroupId) {
    throw new NotFoundError();
  }

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminTransferGroupDetail($id: ID!) {
        node(id: $id) {
          ... on TransferGroup {
            id
            shortRef
            kind
            status
            createdAt
            expectedCompletionDate
            actualCompletionDate
            requestedForDate
            organisation {
              ...OrganisationCardFields
            }
            createdByUser {
              ...UserProfileUI
            }
            totalVolume {
              currency
              amount
            }
            fxTradeLinks {
              id
              kind
              fxTrade {
                id
                externalId
                tradeType
                status
                sellCurrency
                buyCurrency
                sellToBuyRate
                sellAmount { amount currency }
                buyAmount { amount currency }
              }
            }
            ...TransferGroupDescriptionFields
            ...AdminTransferGroup
            ...OperationsTransferGroupActions
          }
        }
      }
    `),
    {
      variables: { id: transferGroupId },
    },
  );

  const transferGroup = data.node;
  if (!transferGroup || transferGroup.__typename !== "TransferGroup") {
    throw new NotFoundError();
  }

  useOpsTitle(`Transfer Group #${transferGroup.shortRef}`);

  const dateEntries: { label: string; date: Date | null | undefined }[] = [
    { label: "Created at", date: transferGroup.createdAt },
    { label: "Requested for", date: transferGroup.requestedForDate },
    {
      label: "Expected completion",
      date: transferGroup.expectedCompletionDate,
    },
    { label: "Actual completion", date: transferGroup.actualCompletionDate },
  ];

  return (
    <AdminPageContainer>
      <AdminPageHeader title="Transfer Group" />
      <BreadcrumbArray
        stack={[
          { title: "Transfer Groups", to: "/transfer-groups" },
          {
            title: <span className="font-mono">#{transferGroup.shortRef}</span>,
          },
        ]}
      />
      <div className="flex flex-col gap-6">
        <dl className="grid grid-cols-2 gap-4 rounded-md border p-4 text-sm md:grid-cols-4">
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Organisation</dt>
            <dd>
              <OrganisationCard
                organisation={transferGroup.organisation}
                linkTo={`/organisations/${transferGroup.organisation.id}`}
              />
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Created by</dt>
            <dd>
              {transferGroup.createdByUser ? (
                <UserProfile {...transferGroup.createdByUser} />
              ) : (
                "-"
              )}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Kind</dt>
            <dd className="text-neutral-700">
              {prettyPrintEnum(transferGroup.kind)}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Status</dt>
            <dd>
              {transferGroup.status && (
                <StatusUI
                  status={uppercaseStatusToStatus[transferGroup.status]}
                />
              )}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Total volume</dt>
            <dd>
              {transferGroup.totalVolume ? (
                <CurrencyAmountUI {...transferGroup.totalVolume} />
              ) : (
                "-"
              )}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="font-medium text-neutral-500">Description</dt>
            <dd className="text-neutral-700">
              <TransferGroupDescription transferGroup={transferGroup} />
            </dd>
          </div>
        </dl>

        <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
          <div className="font-semibold text-base">Timeline</div>
          <div>
            {dateEntries.map(({ label, date }) => (
              <DetailSubRow
                key={label}
                label={label}
                value={
                  date ? (
                    <DateUI date={date} variant="DATETIME" />
                  ) : (
                    <span className="text-neutral-400">—</span>
                  )
                }
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-md border p-4 text-sm">
          <div className="font-semibold text-base">Actions</div>
          <PaneActions {...transferGroup} />
        </div>

        <div className="rounded-md border p-4">
          <h3 className="mb-2 font-semibold font-title">FX Trades</h3>
          {transferGroup.fxTradeLinks &&
          transferGroup.fxTradeLinks.length > 0 ? (
            <div className="divide-y">
              {transferGroup.fxTradeLinks.map((link) => (
                <div
                  key={link.fxTrade?.id}
                  className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center rounded bg-zinc-100 px-2 py-0.5 font-medium font-mono text-xs text-zinc-700">
                      {link.kind}
                    </span>
                    {link.fxTrade && (
                      <div className="flex flex-col gap-0.5">
                        <Link
                          to={`/fx-trades/${link.fxTrade.id}`}
                          className="font-medium text-sm hover:underline"
                        >
                          {prettyPrintEnum(link.fxTrade.tradeType)} ·{" "}
                          {new Decimal(
                            link.fxTrade.sellAmount.amount,
                          ).toFixed()}{" "}
                          {link.fxTrade.sellCurrency} →{" "}
                          {new Decimal(link.fxTrade.buyAmount.amount).toFixed()}{" "}
                          {link.fxTrade.buyCurrency} (1{" "}
                          {link.fxTrade.sellCurrency} ={" "}
                          {new Decimal(link.fxTrade.sellToBuyRate).toFixed()}{" "}
                          {link.fxTrade.buyCurrency})
                        </Link>
                        {link.fxTrade.externalId && (
                          <span className="font-mono text-xs text-zinc-500">
                            {link.fxTrade.externalId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {link.fxTrade?.status && (
                      <StatusUI
                        status={uppercaseStatusToStatus[link.fxTrade.status]}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">
              No FX trades linked to this transfer group.
            </p>
          )}
        </div>

        <TransferGroupUI
          transferGroup={transferGroup}
          currentFundMovementId={null}
        />
      </div>
    </AdminPageContainer>
  );
}
